import type {
  MockCache,
  CallId,
  ChannelId,
  Call,
  MessageId,
} from '../../types';

import { richParagraph } from '../../rich-doc';
import { fanoutLast } from '../fanout';
import { logActivity } from '../journal';
import { insertMessageRow } from './messages';
import { bump, requireCache } from '../runtime';

function withoutMe(cache: MockCache, call: Call): Call {
  const now = cache.nowMs();
  const participants = call.participants.filter((p) => p.userId !== cache.me);
  return {
    ...call,
    participants,
    attendance: call.attendance.map((a) => (a.userId === cache.me && a.leftAt === null ? { ...a, leftAt: now } : a)),
    endedAt: participants.length === 0 ? now : call.endedAt,
  };
}

function leaveOtherCalls(cache: MockCache, keepId: CallId | null): ChannelId[] {
  const touched: ChannelId[] = [];
  for (const call of cache.calls.values()) {
    if (call.endedAt !== null || call.id === keepId) continue;
    if (!call.participants.some((p) => p.userId === cache.me)) continue;
    cache.calls.set(call.id, withoutMe(cache, call));
    touched.push(call.channelId);
  }
  return touched;
}

function putCall(cache: MockCache, call: Call): void {
  cache.calls.set(call.id, call);
}

export function startCall(args: { id: CallId; messageId: MessageId; channelId: ChannelId }): Call {
  const cache = requireCache();
  const channel = cache.channels.get(args.channelId);
  if (channel === undefined) {
    throw new Error('Канал не найден');
  }
  for (const call of cache.calls.values()) {
    if (call.channelId === args.channelId && call.endedAt === null) {
      throw new Error('В этом канале уже идёт звонок');
    }
  }
  const alsoTouched = leaveOtherCalls(cache, null);
  const now = cache.nowMs();
  const message = insertMessageRow(cache, {
    id: args.messageId,
    channelId: args.channelId,
    threadRootId: null,
    replyToId: null,
    bodyJson: richParagraph('Звонок'),
    sendAt: null,
    systemKind: 'call',
    authorId: cache.me,
  });
  const call: Call = {
    id: args.id,
    channelId: args.channelId,
    rootMessageId: message.id,
    startedBy: cache.me,
    startedAt: now,
    endedAt: null,
    participants: [{ userId: cache.me, joinedAt: now, micOn: true, cameraOn: false }],
    attendance: [{ userId: cache.me, joinedAt: now, leftAt: null }],
  };
  putCall(cache, call);
  logActivity(cache, {
    mutator: 'call.start',
    entityTable: 'message',
    entityId: String(message.id),
    op: 'insert',
    before: null,
    after: { callId: String(call.id), channelId: String(call.channelId) },
  });
  fanoutLast(cache);
  bump('calls', `channel:${String(args.channelId)}`, ...alsoTouched.map((id) => `channel:${String(id)}`));
  return call;
}

export function joinCall(args: { id: CallId }): void {
  const cache = requireCache();
  const call = cache.calls.get(args.id);
  if (call === undefined || call.endedAt !== null) {
    throw new Error('Звонок не найден или уже завершён');
  }
  const alsoTouched = leaveOtherCalls(cache, call.id);
  if (!call.participants.some((p) => p.userId === cache.me)) {
    const now = cache.nowMs();
    putCall(cache, {
      ...call,
      participants: [...call.participants, { userId: cache.me, joinedAt: now, micOn: true, cameraOn: false }],
      attendance: [...call.attendance, { userId: cache.me, joinedAt: now, leftAt: null }],
    });
  }
  bump('calls', `channel:${String(call.channelId)}`, ...alsoTouched.map((id) => `channel:${String(id)}`));
}

export function leaveCall(args: { id: CallId }): void {
  const cache = requireCache();
  const call = cache.calls.get(args.id);
  if (call === undefined) return;
  putCall(cache, withoutMe(cache, call));
  bump('calls', `channel:${String(call.channelId)}`);
}

export function setCallDevice(args: { id: CallId; micOn?: boolean; cameraOn?: boolean }): void {
  const cache = requireCache();
  const call = cache.calls.get(args.id);
  if (call === undefined) return;
  if (!call.participants.some((p) => p.userId === cache.me)) return;
  putCall(cache, {
    ...call,
    participants: call.participants.map((p) =>
      p.userId === cache.me
        ? { ...p, micOn: args.micOn ?? p.micOn, cameraOn: args.cameraOn ?? p.cameraOn }
        : p,
    ),
  });
  bump('calls', `channel:${String(call.channelId)}`);
}
